// Grant 2026-11-02: members read the club's own words for its courts, tables
// and boards, never "playing area" ("court", "court or board", "courts and
// boards"), unless nothing names anything. One helper for every member-facing
// surface (/book, the booking sheets, the match scheduling picker, the club
// page's availability card, When & where, widgets), so no surface repeats
// the ternary. The API's own words win when it has them (availabilityTitle /
// areaNoun / areaNounPlural / bookLabel, PlayingAreaNouns on the API); the
// areas on hand fill the gap (their sports' nouns, else the noun in their own
// name: "Court 1" is a court); "playing area" is the last resort. A leaf
// module: nothing here imports another util, so every util may import it.

export const AREA_NOUN = 'playing area';
export const AREA_NOUN_PLURAL = 'playing areas';
export const AREA_TITLE = 'Playing area availability';
export const AREA_BOOK_LABEL = 'Book a playing area';

/** The words a playing area's own name can carry ("R4 Table", "X41 Board 1", "Centre Court"), when its sports don't say. The API's PlayingAreaNouns.NameNouns. */
export const NAME_NOUNS = Object.freeze(['court', 'table', 'board', 'pitch', 'lane', 'rink', 'green', 'bay', 'studio', 'room', 'hall', 'field', 'pool', 'cage', 'net', 'oche', 'mat', 'arena', 'sheet']);

export const cap = (text) => (text ? String(text).charAt(0).toUpperCase() + String(text).slice(1) : '');

/** True for the fallback word itself (or nothing): "playing area" / "Playing areas". */
export const isAreaFallback = (word) => !word || /^playing areas?$/i.test(String(word).trim());

/** "court" → "courts", "pitch" → "pitches", "bay" → "bays" (the API's PlayingAreaNouns.Pluralise). */
export function pluralise(noun) {
  const n = String(noun ?? '').trim();
  if (!n) return n;
  if (/(s|x|z|ch|sh)$/i.test(n)) return n + 'es';
  if (n.length > 1 && /y$/i.test(n) && !/[aeiou]y$/i.test(n)) return n.slice(0, -1) + 'ies';
  return n + 's';
}

/** "courts" → "court", "pitches" → "pitch", "playing areas" → "playing area": the way back from an API plural. */
export function singularise(plural) {
  const p = String(plural ?? '').trim();
  if (!p) return p;
  if (/ies$/i.test(p)) return p.slice(0, -3) + 'y';
  if (/(ches|shes|sses|xes|zes)$/i.test(p)) return p.slice(0, -2);
  if (/s$/i.test(p)) return p.slice(0, -1);
  return p;
}

/** "a court" / "an oche" / "a court or board": the article from the first sound. */
export function aOrAn(words) {
  const w = String(words ?? '').trim();
  if (!w) return '';
  return (/^[aeiou]/i.test(w) ? 'an ' : 'a ') + w;
}

/** The last noun in an area's name ("R4 Table" → "table", "Courts 1-3" → "court"), or null. */
export function nounFromName(name) {
  const words = String(name ?? '').toLowerCase().split(/[^a-z]+/).filter(Boolean);
  let found = null;
  for (const w of words) {
    if (NAME_NOUNS.includes(w)) found = w;
    else if (NAME_NOUNS.includes(w.replace(/s$/, ''))) found = w.replace(/s$/, '');
  }
  return found;
}

/** "court" · "court or board" · "court, table or board" (joiner "or", "and" or "&"). */
export function joinNouns(nouns, joiner = 'or') {
  const list = (nouns ?? []).filter(Boolean);
  if (!list.length) return '';
  if (list.length === 1) return list[0];
  return `${list.slice(0, -1).join(', ')} ${joiner} ${list[list.length - 1]}`;
}

/**
 * The distinct nouns a set of areas names, in the areas' own order: each
 * area's sports' nouns (availability.sports' venueNoun by sportIds), else the
 * noun in its own name; only the bookable ones when any can be booked (the
 * API's PlayingAreaNouns.Nouns rule). An area that names nothing is dropped:
 * "playing area" is the word for no noun, not a noun of its own. [] when
 * nothing names anything.
 */
export function areaNounsOf(areas, sports = []) {
  const all = [...(areas ?? [])].sort((a, b) => (a?.sort ?? 0) - (b?.sort ?? 0));
  const bookable = all.filter((a) => a?.canBeBooked);
  const nouns = [];
  const plurals = {};
  for (const a of bookable.length ? bookable : all) {
    const own = (a?.sportIds ?? []).map((id) => (sports ?? []).find((s) => s.id === id)).filter(Boolean)
        .filter((s) => !isAreaFallback(s.venueNoun)).map((s) => ({noun: String(s.venueNoun).trim().toLowerCase(), plural: String(s.venueNounPlural || '').trim().toLowerCase()}));
    if (!own.length) {
      const fromName = nounFromName(a?.name);
      if (fromName) own.push({noun: fromName, plural: ''});
    }
    for (const o of own) {
      if (!nouns.includes(o.noun)) nouns.push(o.noun);
      if (o.plural && !plurals[o.noun]) plurals[o.noun] = o.plural;
    }
  }
  return nouns.map((n) => ({noun: n, plural: plurals[n] || pluralise(n)}));
}

const fromNouns = (found) => {
  const nouns = found.map((f) => f.noun);
  const noun = joinNouns(nouns, 'or');
  return {
    noun, plural: joinNouns(found.map((f) => f.plural), 'and'), title: `${cap(joinNouns(nouns, '&'))} availability`, bookLabel: `Book ${aOrAn(noun)}`, nouns, known: true,
  };
};

const FALLBACK = Object.freeze({noun: AREA_NOUN, plural: AREA_NOUN_PLURAL, title: AREA_TITLE, bookLabel: AREA_BOOK_LABEL, nouns: [], known: false});

/**
 * The words for an availability response (the member availability, the match
 * scheduling availability, a widget payload: anything with `areas`, and
 * `areaNoun` / `areaNounPlural` / `availabilityTitle` / `bookLabel` when the
 * API says them):
 *   {noun, plural, title, bookLabel, nouns, known}
 *   noun      "court" · "court or board" · "playing area"
 *   plural    "courts" · "courts and boards" · "playing areas"
 *   title     "Court availability" · "Court & board availability" · "Playing area availability"
 *   bookLabel "Book a court" · "Book a court or board" · "Book an oche" · "Book a playing area"
 *   known     false only when nothing names anything (the fallback words)
 * `areas` narrows it to a subset (one tab's areas, one sport's): then the
 * subset decides, the API's club-wide words stand in only when it names
 * nothing.
 */
export function areaWords(availability, {areas = null, sports = null} = {}) {
  const api = availability && !isAreaFallback(availability.areaNoun) ? {
    noun: String(availability.areaNoun).trim(),
    plural: isAreaFallback(availability.areaNounPlural) ? pluralise(String(availability.areaNoun).trim()) : String(availability.areaNounPlural).trim(),
    title: availability.availabilityTitle && !isAreaFallback(String(availability.availabilityTitle).replace(/ availability$/i, '')) ? availability.availabilityTitle : `${cap(availability.areaNoun)} availability`,
    bookLabel: availability.bookLabel && !/playing area/i.test(availability.bookLabel) ? availability.bookLabel : `Book ${aOrAn(availability.areaNoun)}`,
    nouns: String(availability.areaNoun).split(/,\s*|\s+or\s+/).filter(Boolean), known: true,
  } : null;
  if (api && !areas) return api;
  const found = areaNounsOf(areas ?? availability?.areas ?? [], sports ?? availability?.sports ?? []);
  if (found.length) return fromNouns(found);
  if (api) return api;
  if (availability?.availabilityTitle && !isAreaFallback(String(availability.availabilityTitle).replace(/ availability$/i, ''))) return {...FALLBACK, title: availability.availabilityTitle};
  return FALLBACK;
}

/** The words from area names alone ("Court 1", "Table 5"): a booking summary's area, a playing-area list without sports. */
export function areaWordsFromNames(names) {
  const found = areaNounsOf((names ?? []).map((name) => ({name})));
  return found.length ? fromNouns(found) : FALLBACK;
}

/** The label under an area's name ("Court 2" → "Court"), "Playing area" when its name says nothing. */
export function areaLabelFor(name) {
  return cap(nounFromName(name) ?? AREA_NOUN);
}
